import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Wallet, CreditCard, Save, RefreshCw, User, Landmark, Loader2, Banknote, ImageIcon } from "lucide-react";
import { checkBakongStatus, fetCashOnDelivery, fetchBakongSetting, fetchEnableBakong, fetchEnableABA, generateBakongSample, updateBakongSetting, updateCod, updateEnableBakong, updateEnableABA, fetchAbaSetting, updateAbaSetting, generateAbaSample, checkAbaStatus } from '@/api/setting-api';
import { toast } from '@/hooks/use-toast';
import { BakongDetails, SampleBakong, AbaDetails } from '@/types/setting-type';

export function PaymentSettingCard() {
    // Tracking specific loading states for each toggle
    const [loadingStates, setLoadingStates] = useState({
        cod: false,
        bakongToggle: false,
        abaToggle: false,
        bakongDetails: false,
        abaDetails: false,
        generateBakong: false,
        generateAba: false,
        checkStatus: false,
        checkAbaStatus: false
    });

    const [sampleBakong, setSampleBakong] = useState<SampleBakong | null>(null);
    const [sampleAba, setSampleAba] = useState<SampleBakong | null>(null);
    const [cashOnDelivery, setCashOnDelivery] = useState(false);
    const [enableBakong, setEnableBakong] = useState(false);
    const [enableAba, setEnableAba] = useState(false);
    const [bakongDetails, setBakongDetails] = useState<BakongDetails>({
        accountId: "",
        currency: "USD",
        merchantName: ""
    });
    const [abaDetails, setAbaDetails] = useState<AbaDetails>({
        merchantId: "",
        currency: "USD",
        merchantName: ""
    });

    useEffect(() => {
        const init = async () => {
            await Promise.all([
                getCashOnDeliverySetting(),
                getEnableBakongSetting(),
                getEnableABASetting(),
                getBakongAccountSetting(),
                getAbaAccountSetting()
            ]);
        };
        init();
    }, []);

    const getCashOnDeliverySetting = async () => {
        const resp = await fetCashOnDelivery();
        if (resp.success) setCashOnDelivery(resp.data);
    };

    const getEnableBakongSetting = async () => {
        const resp = await fetchEnableBakong();
        if (resp.success) setEnableBakong(resp.data);
    };

    const getEnableABASetting = async () => {
        const resp = await fetchEnableABA();
        if (resp.success) setEnableAba(resp.data);
    };

    const getBakongAccountSetting = async () => {
        const resp = await fetchBakongSetting();
        if (resp.success) setBakongDetails(resp.data);
    };

    const getAbaAccountSetting = async () => {
        const resp = await fetchAbaSetting();
        if (resp.success) setAbaDetails(resp.data);
    };

    const handleUpdateCashOnDelivery = async (value: boolean) => {
        setLoadingStates(prev => ({ ...prev, cod: true }));
        const resp = await updateCod(value);
        if (resp.success) {
            setCashOnDelivery(value);
            toast({ title: "Updated", description: "Cash on delivery setting saved." });
        } else {
            toast({ title: "Error", description: "Could not update COD", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, cod: false }));
    };

    const handleEnableBakong = async (value: boolean) => {
        setLoadingStates(prev => ({ ...prev, bakongToggle: true }));
        const resp = await updateEnableBakong(value);
        if (resp.success) {
            await Promise.all([
                getEnableBakongSetting(),
                getEnableABASetting()
            ]);
            toast({ title: "Updated", description: "Bakong wallet status updated." });

        } else {
            toast({ title: "Error", description: "Could not update Bakong status", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, bakongToggle: false }));
    };

    const handleEnableAba = async (value: boolean) => {
        setLoadingStates(prev => ({ ...prev, abaToggle: true }));
        const resp = await updateEnableABA(value);
        if (resp.success) {
            await Promise.all([
                getEnableBakongSetting(),
                getEnableABASetting()
            ]);
            toast({ title: "Updated", description: "ABA Pay status updated." });

        } else {
            toast({ title: "Error", description: "Could not update ABA status", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, abaToggle: false }));
    };

    const handleUpdateBakongAccount = async () => {
        setLoadingStates(prev => ({ ...prev, bakongDetails: true }));
        const resp = await updateBakongSetting(bakongDetails);
        if (resp.success) {
            toast({ title: "Updated", description: "Bakong wallet account updated." });
        } else {
            toast({ title: "Error", description: "Could not update Bakong Account", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, bakongDetails: false }));
    }

    const handleGenerateSampleBakong = async () => {
        setLoadingStates(prev => ({ ...prev, generateBakong: true }));
        const resp = await generateBakongSample();
        if (resp.success) {
            toast({ title: "Updated", description: "Generate bakong sample successfully you can scan test" });
            setSampleBakong(resp.data);
        } else {
            toast({ title: "Error", description: "Could not generate bakong sample", variant: "destructive" });
        }

        setLoadingStates(prev => ({ ...prev, generateBakong: false }));
    }
    const handleCheckStatus = async () => {
        if (!sampleBakong) return;
        setLoadingStates(prev => ({ ...prev, checkStatus: true }));
        const resp = await checkBakongStatus(sampleBakong?.hash);
        if (resp.success) {
            if (resp.data.responseCode == 0) {
                toast({ title: "Updated", description: "Transaction bakong check/validated successfully" });
            }
            else {
                toast({ title: "Check", description: `Bakong error: ${resp.data.responseMessage}` });
            }

        } else {
            toast({ title: "Error", description: "Could not check bakong sample", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, checkStatus: false }));
    }

    const handleUpdateAbaAccount = async () => {
        setLoadingStates(prev => ({ ...prev, abaDetails: true }));
        const resp = await updateAbaSetting(abaDetails);
        if (resp.success) {
            toast({ title: "Updated", description: "ABA Pay account updated." });
        } else {
            toast({ title: "Error", description: "Could not update ABA Account", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, abaDetails: false }));
    }

    const handleGenerateSampleAba = async () => {
        setLoadingStates(prev => ({ ...prev, generateAba: true }));
        const resp = await generateAbaSample();
        if (resp.success) {
            toast({ title: "Updated", description: "Generate ABA sample successfully you can scan test" });
            setSampleAba(resp.data);
        } else {
            toast({ title: "Error", description: "Could not generate ABA sample", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, generateAba: false }));
    }

    const handleCheckAbaStatus = async () => {
        if (!sampleAba) return;
        setLoadingStates(prev => ({ ...prev, checkAbaStatus: true }));
        const resp = await checkAbaStatus(sampleAba?.hash);
        if (resp.success) {
            if (resp.data.responseCode == 0) {
                toast({ title: "Updated", description: "Transaction ABA check/validated successfully" });
            }
            else {
                toast({ title: "Check", description: `ABA error: ${resp.data.responseMessage}` });
            }
        } else {
            toast({ title: "Error", description: "Could not check ABA sample", variant: "destructive" });
        }
        setLoadingStates(prev => ({ ...prev, checkAbaStatus: false }));
    }

    return (
        <Card className="shadow-sm border-slate-200">
            <CardHeader className="pb-4">
                <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <CardTitle>Payment Methods</CardTitle>
                </div>
                <CardDescription>
                    Configure how your customers pay and manage your gateway credentials.
                </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
                <div className="grid gap-4">

                    {/* COD Section */}
                    <div className="group flex items-center justify-between p-4 border rounded-xl hover:bg-slate-50 transition-all duration-200">
                        <div className="flex gap-4 items-start">
                            <div className="p-2 bg-amber-50 rounded-lg text-amber-600 group-hover:scale-110 transition-transform">
                                <Wallet className="w-5 h-5" />
                            </div>
                            <div>
                                <Label className="text-base font-semibold cursor-pointer">Cash on Delivery (COD)</Label>
                                <p className="text-xs text-slate-500">Manual payment upon receipt.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {loadingStates.cod && <Loader2 className="w-4 h-4 animate-spin text-amber-600" />}
                            <Switch
                                checked={cashOnDelivery}
                                onCheckedChange={handleUpdateCashOnDelivery}
                            />
                        </div>
                    </div>

                    {/* Bakong Section */}
                    <div className={`rounded-xl border transition-all duration-300 overflow-hidden ${enableBakong ? 'border-blue-200 shadow-sm' : 'border-slate-200'}`}>
                        <div className={`flex items-center justify-between p-4 transition-colors ${enableBakong ? 'bg-blue-50/30' : 'bg-white'}`}>
                            <div className="flex gap-4 items-start">
                                <div className="p-1.5 bg-white border rounded-lg shadow-sm">
                                    <img src="/bakong.svg" className="w-8 h-6 object-contain bg-red-600" alt="Bakong" />
                                </div>
                                <div>
                                    <Label className="text-base font-semibold cursor-pointer">Bakong Wallet (KHQR)</Label>
                                    <p className="text-xs text-slate-500">Secure KHQR digital payments.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {loadingStates.bakongToggle && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                                <Switch
                                    checked={enableBakong}
                                    onCheckedChange={handleEnableBakong}
                                />
                            </div>
                        </div>

                        {/* Expandable Form */}
                        {enableBakong && (
                            <>
                                <div className="px-4 pb-5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="ml-12 p-5 bg-white border border-blue-100 rounded-lg shadow-inner-sm space-y-5">
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                    <Landmark className="w-3 h-3" /> Bakong Account ID
                                                </Label>
                                                <Input
                                                    className="bg-slate-50/50"
                                                    placeholder="username@bank"
                                                    value={bakongDetails.accountId}
                                                    onChange={(e) => setBakongDetails({ ...bakongDetails, accountId: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Currency</Label>
                                                <Select
                                                    value={bakongDetails.currency}
                                                    onValueChange={(v) => setBakongDetails({ ...bakongDetails, currency: v })}
                                                >
                                                    <SelectTrigger className="bg-slate-50/50">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                                                        <SelectItem value="KHR">KHR - Khmer Riel</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <Label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                    <User className="w-3 h-3" /> Merchant Name
                                                </Label>
                                                <Input
                                                    className="bg-slate-50/50"
                                                    placeholder="Store name shown on QR"
                                                    value={bakongDetails.merchantName}
                                                    onChange={(e) => setBakongDetails({ ...bakongDetails, merchantName: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-center gap-6">

                                            <Button
                                                className="w-fit px-6  h-11 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all"
                                                onClick={handleUpdateBakongAccount}
                                                disabled={loadingStates.bakongDetails}
                                            >
                                                {loadingStates.bakongDetails ? (
                                                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                                ) : (
                                                    <Save className="w-4 h-4 mr-2" />
                                                )}
                                                Save Bakong Wallet Account
                                            </Button>
                                        </div>


                                    </div>
                                </div>
                                <div className="px-4 pb-5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="ml-12 p-5 bg-white border border-blue-100 rounded-lg shadow-inner-sm space-y-5">

                                        <div className="flex flex-col items-center gap-6">
                                            {/* Action Button: Centered and Smaller */}
                                            <Button
                                                size="sm"
                                                className="w-fit px-6 h-9 bg-blue-600 hover:bg-blue-700 active:scale-[0.95] transition-all rounded-full shadow-sm"
                                                onClick={handleGenerateSampleBakong}
                                                disabled={loadingStates.generateBakong}
                                            >
                                                {loadingStates.generateBakong ? (
                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
                                                ) : (
                                                    <Banknote className="w-3.5 h-3.5 mr-2" />
                                                )}
                                                Generate Sample KHQR
                                            </Button>

                                            {/* QR Display Area */}
                                            <div className="relative group">
                                                <div className='w-64 h-64 rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all duration-300 border-slate-200 bg-white shadow-sm'>
                                                    {loadingStates.generateBakong ? (
                                                        <div className="flex flex-col items-center gap-2">
                                                            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                                                            <p className="text-[10px] text-blue-500 font-bold animate-pulse">GENERATING...</p>
                                                        </div>
                                                    ) : sampleBakong ? (
                                                        <img
                                                            src={`data:image/png;base64,${sampleBakong.qrImage}`}
                                                            alt="QR Code"
                                                            className="w-full h-full object-contain p-2 animate-in zoom-in-95 duration-300"
                                                        />
                                                    ) : (
                                                        <div className="text-center px-4">
                                                            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                                            <p className="text-sm text-slate-500 font-medium">No QR Found</p>
                                                            <p className="text-xs text-slate-400">Click button above to preview</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Optional: MD5 Hash Display if sampleBakong exists */}
                                            {sampleBakong && !loadingStates.generateBakong && (
                                                <div className="px-3 py-1 bg-slate-100 rounded-md border border-slate-200">
                                                    <p className="text-[10px] font-mono text-slate-500">
                                                        HASH: {sampleBakong.hash?.substring(0, 16)}...
                                                    </p>

                                                </div>
                                            )}
                                            <Button
                                                size="sm"
                                                className="w-fit px-6 h-9 bg-green-600 hover:bg-green-700 active:scale-[0.95] transition-all rounded-full shadow-sm"
                                                onClick={handleCheckStatus}
                                                disabled={loadingStates.checkStatus}
                                            >
                                                {loadingStates.generateBakong ? (
                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
                                                ) : (
                                                    <Banknote className="w-3.5 h-3.5 mr-2" />
                                                )}
                                                Check Status
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* ABA Section */}
                    <div className={`rounded-xl border transition-all duration-300 overflow-hidden ${enableAba ? 'border-blue-200 shadow-sm' : 'border-slate-200'}`}>
                        <div className={`flex items-center justify-between p-4 transition-colors ${enableAba ? 'bg-blue-50/30' : 'bg-white'}`}>
                            <div className="flex gap-4 items-start">
                                <div className="p-1.5 bg-white border rounded-lg shadow-sm">
                                    <img src="https://www.ababank.com/typo3conf/ext/boxmodel/Resources/Private/Templates/ABA/images/aba-web-top-logo.png" className="w-8 h-6 object-contain" alt="ABA" />
                                </div>
                                <div>
                                    <Label className="text-base font-semibold cursor-pointer">ABA Pay</Label>
                                    <p className="text-xs text-slate-500">Secure ABA digital payments.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {loadingStates.abaToggle && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
                                <Switch
                                    checked={enableAba}
                                    onCheckedChange={handleEnableAba}
                                />
                            </div>
                        </div>

                        {/* Expandable Form */}
                        {enableAba && (
                            <>
                                <div className="px-4 pb-5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="ml-12 p-5 bg-white border border-blue-100 rounded-lg shadow-inner-sm space-y-5">
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                    <Landmark className="w-3 h-3" /> ABA Merchant ID
                                                </Label>
                                                <Input
                                                    className="bg-slate-50/50"
                                                    placeholder="merchant id"
                                                    value={abaDetails.merchantId}
                                                    onChange={(e) => setAbaDetails({ ...abaDetails, merchantId: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Currency</Label>
                                                <Select
                                                    value={abaDetails.currency}
                                                    onValueChange={(v) => setAbaDetails({ ...abaDetails, currency: v })}
                                                >
                                                    <SelectTrigger className="bg-slate-50/50">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                                                        <SelectItem value="KHR">KHR - Khmer Riel</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <Label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                                    <User className="w-3 h-3" /> Merchant Name
                                                </Label>
                                                <Input
                                                    className="bg-slate-50/50"
                                                    placeholder="Store name shown on QR"
                                                    value={abaDetails.merchantName}
                                                    onChange={(e) => setAbaDetails({ ...abaDetails, merchantName: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-center gap-6">

                                            <Button
                                                className="w-fit px-6  h-11 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all"
                                                onClick={handleUpdateAbaAccount}
                                                disabled={loadingStates.abaDetails}
                                            >
                                                {loadingStates.abaDetails ? (
                                                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                                                ) : (
                                                    <Save className="w-4 h-4 mr-2" />
                                                )}
                                                Save ABA Pay Account
                                            </Button>
                                        </div>

                                    </div>
                                </div>
                                <div className="px-4 pb-5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="ml-12 p-5 bg-white border border-blue-100 rounded-lg shadow-inner-sm space-y-5">

                                        <div className="flex flex-col items-center gap-6">
                                            {/* Action Button: Centered and Smaller */}
                                            <Button
                                                size="sm"
                                                className="w-fit px-6 h-9 bg-blue-600 hover:bg-blue-700 active:scale-[0.95] transition-all rounded-full shadow-sm"
                                                onClick={handleGenerateSampleAba}
                                                disabled={loadingStates.generateAba}
                                            >
                                                {loadingStates.generateAba ? (
                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
                                                ) : (
                                                    <Banknote className="w-3.5 h-3.5 mr-2" />
                                                )}
                                                Generate Sample QR
                                            </Button>

                                            {/* QR Display Area */}
                                            <div className="relative group">
                                                <div className='w-64 h-64 rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all duration-300 border-slate-200 bg-white shadow-sm'>
                                                    {loadingStates.generateAba ? (
                                                        <div className="flex flex-col items-center gap-2">
                                                            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
                                                            <p className="text-[10px] text-blue-500 font-bold animate-pulse">GENERATING...</p>
                                                        </div>
                                                    ) : sampleAba ? (
                                                        <img
                                                            src={`${sampleAba.qrImage}`}
                                                            alt="QR Code"
                                                            className="w-full h-full object-contain p-2 animate-in zoom-in-95 duration-300"
                                                        />
                                                    ) : (
                                                        <div className="text-center px-4">
                                                            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                                            <p className="text-sm text-slate-500 font-medium">No QR Found</p>
                                                            <p className="text-xs text-slate-400">Click button above to preview</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Optional: MD5 Hash Display if sampleAba exists */}
                                            {sampleAba && !loadingStates.generateAba && (
                                                <div className="px-3 py-1 bg-slate-100 rounded-md border border-slate-200">
                                                    <p className="text-[10px] font-mono text-slate-500">
                                                        HASH: {sampleAba.hash?.substring(0, 16)}...
                                                    </p>

                                                </div>
                                            )}
                                            <Button
                                                size="sm"
                                                className="w-fit px-6 h-9 bg-green-600 hover:bg-green-700 active:scale-[0.95] transition-all rounded-full shadow-sm"
                                                onClick={handleCheckAbaStatus}
                                                disabled={loadingStates.checkAbaStatus}
                                            >
                                                {loadingStates.checkAbaStatus ? (
                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin mr-2" />
                                                ) : (
                                                    <Banknote className="w-3.5 h-3.5 mr-2" />
                                                )}
                                                Check Status
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}